import * as Sentry from '@sentry/react';
import { ApiError } from '../../api/transport/HttpTransport';
import { ENVIRONMENT_TYPE } from '../../helpers/getEnvironment';

export class LogServiceError {
  private static serviceInstance: LogServiceError;

  errors: Set<Error> = new Set();
 
  private pollingIntervalMs = 3000;

  private static ignoredHttpCodes = [400, 401, 403];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static get instance(): LogServiceError {
    if (!LogServiceError.serviceInstance) {
      LogServiceError.serviceInstance = new LogServiceError();
    }

    return LogServiceError.serviceInstance;
  }

  captureError(error: Error): void {
    this.errors.add(error);
  }

  captureMessage(
    message: string,
    tag: Record<string, string | boolean | number> = {},
    extras: Record<string, unknown> = {},
    level: Sentry.SeverityLevel = 'info',
  ): void {
    Sentry.captureMessage(message, scope => {
      Object.entries(tag).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
      Object.entries(extras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      scope.setLevel(level);
      return scope;
    });
  }
  
  setGlobalExtras(extras: Record<string, unknown>): void {
    Sentry.setExtras(extras);
  }

  static captureNonHttpError = (error: ApiError | Error): void => {
    if (!(error instanceof ApiError)) {
      Sentry.captureException(error);
    }
  };

  private logPendingErrors(): void {
    for (const error of this.errors) {
      LogServiceError.captureNonHttpError(error);
    }
    this.errors.clear();
  }

  async initialize(environment: ENVIRONMENT_TYPE): Promise<void> {
    await this.sentryInit(environment);

    setInterval(() => {
      this.logPendingErrors();
    }, this.pollingIntervalMs);
  }

  private async sentryInit(environment: ENVIRONMENT_TYPE): Promise<void> {
    if (environment !== ENVIRONMENT_TYPE.LOCAL) { // Don't run on local development
      Sentry.init({
        // captureUnhandledRejections,
        beforeSend(event, hint) {
          try {
            // If the error has a status of 400, do not send it to Sentry
            const error = hint?.originalException as { status: number, message: string };
            const shouldIgnore =
              error.status && LogServiceError.ignoredHttpCodes.includes(error.status) ||
              // https://stackoverflow.com/questions/71280168/javascript-typeerror-load-failed-error-when-calling-fetch-on-ios
              !error.status && ['load failed', 'failed to fetch'].includes((error.message || '').toLowerCase());
            if (shouldIgnore) {
              return null;
            }
          } catch {}

          // Continue sending other events to Sentry
          return event;
        },
        denyUrls: [
          // OneTrust Cookie Consent (CMP)
          /cdn\.cookielaw\.org/,
          // Google Adsense
          /pagead\/js/i,
          // Facebook flakiness
          /graph\.facebook\.com/i,
          // Facebook blocked
          /connect\.facebook\.net\/en_US\/all\.js/i,
          // Woopra flakiness
          /eatdifferent\.com\.woopra-ns\.com/i,
          /static\.woopra\.com\/js\/woopra\.js/i,
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          // Other plugins
          /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
        dsn: 'https://5fb09d36c8104a04b6521bf5b80c6750@o1412351.ingest.sentry.io/6777208',
        environment,
        ignoreErrors:[
          'Non-Error exception captured',
          'Non-Error promise rejection captured',
          'TypeError: undefined is not an object (evaluating \'a.L\')',
          // Random plugins/extensions
          'top.GLOBALS',
          'ResizeObserver loop completed with undelivered notifications',
          "undefined is not an object (evaluating 'r.DomainData')",
          'Can not send postrobot_method. Target window is closed',
          'Can\'t find variable: _AutofillCallbackHandler',
          // Safari error only
          'UnhandledRejection: Object captured as promise rejection with keys: data, sentMessage',
          'PerformanceMonitoringJavascriptInterface.onDomContentLoaded is not a function',
          'PerformanceMonitoringJavascriptInterface.resourceLoadTimes is not a function',
          'PerformanceMonitoringJavascriptInterface.onPageReady is not a function',
          'PerformanceMonitoringJavascriptInterface.logPerformanceMetrics is not a function',
        ],
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.thirdPartyErrorFilterIntegration({
            // Defines how to handle errors that contain third party stack frames.
            // Possible values are:
            // - 'drop-error-if-contains-third-party-frames'
            // - 'drop-error-if-exclusively-contains-third-party-frames'
            // - 'apply-tag-if-contains-third-party-frames'
            // - 'apply-tag-if-exclusively-contains-third-party-frames'
            behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
            // Specify the application keys that you specified in the Sentry bundler plugin
            filterKeys: ['estee-pincer-checkout-sentry'],
          }),
        ],
        maxValueLength: 10000,
        release: `fe-elc-react-checkout-app@${process.env.COMMIT_HASH}`,
        tracesSampleRate: environment === ENVIRONMENT_TYPE.PRODUCTION ? 0.1 : 1.0,
      });
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type descriptorType = TypedPropertyDescriptor<(... params: any[])=> Promise<any> | any>;

// usefull to enhance async functions with error capturing for unhandled promise rejections
export const enhanceWithErrorCapture =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (_target: any, _propertyKey: string, descriptor: descriptorType): descriptorType => {
    const originalFunction = descriptor.value;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    descriptor.value = function (...args: any[]) {
      let result;
      try {
        result = originalFunction?.apply(this, args);
        if (result instanceof Promise) {
          result.catch(error => {
            LogServiceError.instance.captureError(error);
            throw error;
          });
        }
      } catch (error) {
        LogServiceError.instance.captureError(error);
        throw error;
      }

      return result;
    };

    return descriptor;
  };
